import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/shared/PageHero";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <PageHero
        title="Terms of Service"
        subtitle="Legal"
        description="Please read these terms carefully before using our website and services."
        variant="vibrant"
      />

      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto prose prose-invert">
            
            {/* Last Updated */}
            <p className="text-muted-foreground text-sm mb-8">
              Last Updated: February 2026
            </p>

            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">1. Acceptance of Terms</h2>
              <div className="text-muted-foreground space-y-4">
                <p>
                  By accessing and using the SDC Gulf website and services, you accept and agree to be bound 
                  by these Terms of Service. If you do not agree to these terms, please do not use our services.
                </p>
                <p>
                  These terms apply to all visitors, users, and others who access or use our services.
                </p>
              </div>
            </div>

            {/* Use of Services */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">2. Use of Services</h2>
              <div className="text-muted-foreground space-y-4">
                <p>You agree to use our services only for lawful purposes and in accordance with these Terms. You agree not to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Use our services in any way that violates applicable laws or regulations</li>
                  <li>Engage in any conduct that restricts or inhibits anyone's use of our services</li>
                  <li>Attempt to gain unauthorized access to our systems or networks</li>
                  <li>Use any automated means to access our services without our express permission</li>
                  <li>Transmit any malicious code, viruses, or harmful content</li>
                  <li>Impersonate or misrepresent your affiliation with any person or entity</li>
                </ul>
              </div>
            </div>

            {/* Intellectual Property */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">3. Intellectual Property Rights</h2>
              <div className="text-muted-foreground space-y-4">
                <p>
                  The content, features, and functionality of our website and services are owned by SDC Gulf 
                  and are protected by international copyright, trademark, patent, and other intellectual 
                  property laws.
                </p>
                <p>This includes but is not limited to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Text, graphics, logos, and images</li>
                  <li>Software, code, and technical specifications</li>
                  <li>Product designs and documentation</li>
                  <li>Marketing materials and brand assets</li>
                  <li>All other content and materials on our website</li>
                </ul>
                <p className="mt-4">
                  You may not reproduce, distribute, modify, or create derivative works without our prior 
                  written consent.
                </p>
              </div>
            </div>

            {/* Services Description */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">4. Products and Services</h2>
              <div className="text-muted-foreground space-y-4">
                <p>
                  SDC Gulf provides sustainable mobility solutions including electric vehicles, fleet 
                  management services, and related products for government agencies and corporate clients.
                </p>
                <p>
                  All product descriptions, specifications, and pricing are subject to change without notice. 
                  We reserve the right to modify or discontinue any product or service at any time.
                </p>
                <p>
                  Specific terms for product purchases, leasing agreements, and service contracts will be 
                  provided in separate agreements.
                </p>
              </div>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">5. Limitation of Liability</h2>
              <div className="text-muted-foreground space-y-4">
                <p>
                  To the fullest extent permitted by applicable law, SDC Gulf shall not be liable for any 
                  indirect, incidental, special, consequential, or punitive damages, including but not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Loss of profits, revenue, or data</li>
                  <li>Business interruption</li>
                  <li>Personal injury or property damage</li>
                  <li>Any other damages arising from your use of our services</li>
                </ul>
                <p className="mt-4">
                  Our total liability shall not exceed the amount paid by you, if any, for accessing our services.
                </p>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">6. Disclaimer of Warranties</h2>
              <div className="text-muted-foreground space-y-4">
                <p>
                  Our website and services are provided on an "AS IS" and "AS AVAILABLE" basis without 
                  warranties of any kind, either express or implied.
                </p>
                <p>We do not warrant that:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Our services will be uninterrupted, secure, or error-free</li>
                  <li>The results obtained from our services will be accurate or reliable</li>
                  <li>Any errors or defects will be corrected</li>
                </ul>
              </div>
            </div>

            {/* Indemnification */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">7. Indemnification</h2>
              <div className="text-muted-foreground space-y-4">
                <p>
                  You agree to defend, indemnify, and hold harmless SDC Gulf and its officers, directors, 
                  employees, and agents from any claims, damages, losses, liabilities, costs, or expenses 
                  arising out of:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Your use of our services</li>
                  <li>Your violation of these Terms</li>
                  <li>Your violation of any third-party rights</li>
                  <li>Any content you submit or transmit through our services</li>
                </ul>
              </div>
            </div>

            {/* Governing Law */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">8. Governing Law</h2>
              <div className="text-muted-foreground space-y-4">
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of the Kingdom 
                  of Saudi Arabia, without regard to its conflict of law provisions.
                </p>
                <p>
                  Any disputes arising from these Terms or your use of our services shall be subject to the 
                  exclusive jurisdiction of the courts of Saudi Arabia.
                </p>
              </div>
            </div>

            {/* Termination */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">9. Termination</h2>
              <div className="text-muted-foreground space-y-4">
                <p>
                  We reserve the right to terminate or suspend your access to our services immediately, 
                  without prior notice or liability, for any reason, including breach of these Terms.
                </p>
                <p>
                  Upon termination, your right to use our services will cease immediately.
                </p>
              </div>
            </div>

            {/* Changes to Terms */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">10. Changes to Terms</h2>
              <div className="text-muted-foreground space-y-4">
                <p>
                  We reserve the right to modify these Terms at any time. Changes will be effective 
                  immediately upon posting on our website.
                </p>
                <p>
                  Your continued use of our services after any changes constitutes acceptance of the new Terms.
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">11. Contact Us</h2>
              <div className="text-muted-foreground space-y-4">
                <p>
                  If you have any questions about these Terms, please contact us:
                </p>
                <ul className="list-none space-y-2">
                  <li><strong className="text-foreground">Email:</strong> info@sdcgulf.com</li>
                  <li><strong className="text-foreground">Phone:</strong> +966 5 737 505 48</li>
                  <li><strong className="text-foreground">Address:</strong> Jeddah, Kingdom of Saudi Arabia</li>
                </ul>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfService;
